import { Injectable } from '@angular/core';
import { IAgencyDetailsState } from './agency-details-store.state';
import { Store } from '@ngrx/store';
import * as AgencyDetailsActions from './agency-details-store.actions';
import { Observable, filter, map } from 'rxjs';
import * as AgencyDetailsSelectors from './agency-details-store.selectors';
import { AgencyDetails, AgencyDetailsUpdateObject, AgencySettings, AgencySettingsUpdateRequest } from '../models/agency-details.models';
import { NewCategoryRequest, UpdateCategoryRequest, Category } from '../models/agency-category.models';
import { ActionType, AgencyActionResult } from '../models/agency-action-result.models';

@Injectable({
  providedIn: 'root',
})
export class AgencyDetailsFacade {
  public constructor(private readonly store: Store<IAgencyDetailsState>) {}

  public fetchAgencyDetails(): void {
    this.store.dispatch(AgencyDetailsActions.fetchAgencyDetails());
  }

  public getAgencyDetails(): Observable<AgencyDetails> {
    return this.store.select(AgencyDetailsSelectors.agencyDetailsState).pipe(filter((x): x is AgencyDetails => !!x));
  }

  public getAgencyUid(): Observable<string> {
    return this.store.select(AgencyDetailsSelectors.agencyUid).pipe(filter((x): x is string => !!x));
  }

  public fetchAgencyCategories(): void {
    this.store.dispatch(AgencyDetailsActions.fetchAgencyCategories());
  }

  public getAgencyCategories(): Observable<Category[]> {
    return this.store.select(AgencyDetailsSelectors.agencyCategories);
  }

  public fetchAgencySettings(): void {
    this.store.dispatch(AgencyDetailsActions.fetchAgencySettings());
  }

  public getAgencySettings(): Observable<AgencySettings> {
    return this.store.select(AgencyDetailsSelectors.agencySettings).pipe(filter((x): x is AgencySettings => !!x));
  }

  public updateAgencyPhoto(photo: File): void {
    this.store.dispatch(AgencyDetailsActions.updateAgencyPhoto({ photo: photo }));
  }

  public updateAgencyDetails(details: AgencyDetailsUpdateObject): void {
    this.store.dispatch(AgencyDetailsActions.updateAgencyDetails({ details: details }));
  }

  public updatePointOfContact(details: AgencyDetailsUpdateObject): void {
    this.store.dispatch(AgencyDetailsActions.updatePointOfContact({ details: details }));
  }

  public updateSettings(settings: AgencySettingsUpdateRequest): void {
    this.store.dispatch(AgencyDetailsActions.updateAgencySettings({ settings: settings }));
  }

  public getAgencyPhoto(): Observable<string | null> {
    return this.store.select(AgencyDetailsSelectors.agencyPhoto).pipe(map((x) => x ?? null));
  }

  public createNewAgencyCategory(category: NewCategoryRequest): void {
    this.store.dispatch(AgencyDetailsActions.addNewAgencyCategory({ category: category }));
  }

  public updateAgencyCategory(uid: string, category: UpdateCategoryRequest): void {
    this.store.dispatch(AgencyDetailsActions.updateAgencyCategory({ uid: uid, category: category }));
  }

  public deleteAgencyCategory(uid: string): void {
    this.store.dispatch(AgencyDetailsActions.deleteAgencyCategory({ uid: uid }));
  }

  public isLoading(): Observable<boolean> {
    return this.store.select(AgencyDetailsSelectors.isLoading);
  }

  public isImageLoading(): Observable<boolean> {
    return this.store.select(AgencyDetailsSelectors.isImageLoading);
  }

  public agencyActionResult(actionType: ActionType): Observable<AgencyActionResult> {
    return this.store.select(AgencyDetailsSelectors.actionResult).pipe(filter((x): x is AgencyActionResult => !!x && x.actionType === actionType));
  }
}
